import { isMemoriesHost, mainAppOrigin, memoriesPublicBase, MEMORIES_GUEST_PROFILE_KEY, MEMORIES_GUEST_TOKEN_KEY } from './memoriesHost';
import api from '../config/api';
import { clearClientSessionBranding } from '../contexts/ClientBrandingContext';
import { clearAllTripSnapshots } from './offlineTripStore';
import { clearOfflineApiCache } from './registerServiceWorker';

const HANDOFF_FLAG = 'memoriesAuthHandoff';
const LOGOUT_GUARD = 'dvn_memories_logging_out';

/** Mark that Memories is intentionally signing out (blocks auth re-bridge). */
export function beginMemoriesLogout() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(LOGOUT_GUARD, String(Date.now()));
  }
}

export function clearMemoriesLogoutGuard() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(LOGOUT_GUARD);
  }
}

export function isMemoriesLogoutInProgress() {
  if (typeof sessionStorage === 'undefined') return false;
  const raw = sessionStorage.getItem(LOGOUT_GUARD);
  if (!raw) return false;
  const startedAt = Number(raw);
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > 120000) {
    sessionStorage.removeItem(LOGOUT_GUARD);
    return false;
  }
  return true;
}

/** Safe absolute return URL on the memories host only. */
export function sanitizeMemoriesReturnUrl(raw, fallbackPath = '/') {
  const memoriesBase = memoriesPublicBase({ tripLinked: false }).replace(/\/$/, '');
  const path = fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`;
  const fallback = `${memoriesBase}${path === '/' ? '/' : path}`;
  if (!raw || typeof raw !== 'string') return fallback;
  const trimmed = raw.trim();
  if (trimmed === memoriesBase || trimmed === `${memoriesBase}/`) {
    return `${memoriesBase}/`;
  }
  if (trimmed.startsWith(`${memoriesBase}/`)) {
    return trimmed.split('#')[0];
  }
  return fallback;
}

/** Safe absolute return URL on the main app origin only. */
export function sanitizeMainReturnUrl(raw, fallbackPath = '/') {
  const mainBase = mainAppOrigin().replace(/\/$/, '');
  const path = fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`;
  const fallback = `${mainBase}${path === '/' ? '/' : path}`;
  if (!raw || typeof raw !== 'string') return fallback;
  const trimmed = raw.trim();
  if (trimmed === mainBase || trimmed === `${mainBase}/`) {
    return `${mainBase}/`;
  }
  if (trimmed.startsWith(`${mainBase}/`)) {
    return trimmed.split('#')[0];
  }
  return fallback;
}

export function memoriesHostOrigin() {
  return memoriesPublicBase({ tripLinked: false }).replace(/\/$/, '');
}

/** True when Memories and the main app use different origins (subdomain setup). */
export function memoriesUsesSeparateOrigin() {
  if (typeof window === 'undefined') return false;
  try {
    const memoriesOrigin = new URL(memoriesHostOrigin()).origin;
    const mainOrigin = new URL(mainAppOrigin()).origin;
    return memoriesOrigin !== mainOrigin;
  } catch {
    return false;
  }
}

/** Drop traveler + guest session keys on this origin. */
export function clearMemoriesLocalSession() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem(MEMORIES_GUEST_TOKEN_KEY);
  localStorage.removeItem(MEMORIES_GUEST_PROFILE_KEY);
  if (api?.defaults?.headers?.common) {
    delete api.defaults.headers.common['X-Guest-Token'];
    delete api.defaults.headers.common.Authorization;
  }
}

/** Main-app URL that copies the traveler session onto the memories host. */
export function memoriesAuthBridgeUrl(returnUrl) {
  const ret = sanitizeMemoriesReturnUrl(returnUrl);
  return `${mainAppOrigin()}/auth/memories-bridge?return=${encodeURIComponent(ret)}`;
}

/**
 * After clearing Memories local storage, hit the main app to clear that
 * session too, then return to a safe main or memories URL.
 */
export function memoriesCrossLogoutUrl(returnUrl) {
  const ret = sanitizeLogoutReturnUrl(returnUrl);
  return `${mainAppOrigin()}/auth/memories-logout?return=${encodeURIComponent(ret)}`;
}

/**
 * After clearing main-app local storage, hit Memories to clear that origin's
 * session too, then return to the main app.
 */
export function mainAppCrossLogoutUrl(returnUrl) {
  const ret = sanitizeLogoutReturnUrl(returnUrl);
  return `${memoriesHostOrigin()}/auth/logout-clear?return=${encodeURIComponent(ret)}`;
}

/** Allow post-logout redirects to either the main app or Memories host. */
export function sanitizeLogoutReturnUrl(raw) {
  if (!raw || typeof raw !== 'string') {
    return `${mainAppOrigin()}/`;
  }
  const trimmed = raw.trim().split('#')[0];
  const memoriesBase = memoriesHostOrigin();
  const mainBase = mainAppOrigin().replace(/\/$/, '');
  if (trimmed === memoriesBase || trimmed === `${memoriesBase}/` || trimmed.startsWith(`${memoriesBase}/`)) {
    return trimmed === memoriesBase ? `${memoriesBase}/` : trimmed;
  }
  if (trimmed === mainBase || trimmed === `${mainBase}/` || trimmed.startsWith(`${mainBase}/`)) {
    return trimmed === mainBase ? `${mainBase}/` : trimmed;
  }
  return `${mainBase}/`;
}

/** Login / register entry that returns the visitor to this share or join link. */
export function memoriesAuthEntryUrls(returnUrl) {
  let absoluteReturn = typeof returnUrl === 'string' ? returnUrl.trim() : '';
  if (!absoluteReturn && typeof window !== 'undefined' && window.location?.href) {
    absoluteReturn = window.location.href;
  }
  if (!absoluteReturn) absoluteReturn = '/';

  if (isMemoriesHost()) {
    if (!/^https?:\/\//i.test(absoluteReturn) && typeof window !== 'undefined' && window.location?.origin) {
      absoluteReturn = `${window.location.origin}${absoluteReturn.startsWith('/') ? '' : '/'}${absoluteReturn}`;
    }
    const sanitized = sanitizeMemoriesReturnUrl(absoluteReturn);
    const bridgeNext = `/auth/memories-bridge?return=${encodeURIComponent(sanitized)}`;
    return {
      login: memoriesAuthBridgeUrl(sanitized),
      register: `${mainAppOrigin()}/register?next=${encodeURIComponent(bridgeNext)}`,
    };
  }

  let path = absoluteReturn;
  const absoluteMatch = absoluteReturn.match(/^https?:\/\/[^/]+(\/[^#]*)?/i);
  if (absoluteMatch) {
    path = absoluteMatch[1] || '/';
  } else if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  return {
    login: `/login?next=${encodeURIComponent(path)}`,
    register: `/register?next=${encodeURIComponent(path)}`,
  };
}

/** Memories URL that accepts a one-time session handoff in the hash. */
export function memoriesAuthHandoffUrl(token, returnUrl) {
  const ret = sanitizeMemoriesReturnUrl(returnUrl);
  const hash = `token=${encodeURIComponent(String(token || ''))}&next=${encodeURIComponent(ret)}`;
  return `${memoriesPublicBase({ tripLinked: false })}/auth/handoff#${hash}`;
}

/** Send an unauthenticated memories visitor to the shared main-app login/session. */
export function redirectMemoriesToSharedLogin(returnUrl = typeof window !== 'undefined' ? window.location.href : '/') {
  if (typeof window === 'undefined') return;
  // If logout already cleared this origin, never re-bridge a main-app JWT.
  if (isMemoriesLogoutInProgress()) {
    window.location.replace(memoriesCrossLogoutUrl(`${mainAppOrigin()}/login`));
    return;
  }
  window.location.assign(memoriesAuthBridgeUrl(returnUrl));
}

/** Drop traveler session keys on this origin without touching React auth state. */
export function clearMainAppLocalSession() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  clearClientSessionBranding();
  clearAllTripSnapshots().catch(() => {});
  clearOfflineApiCache();
  if (api?.defaults?.headers?.common) {
    delete api.defaults.headers.common.Authorization;
  }
}

/**
 * Hard-navigate logout from the main app: revoke sessions, clear this origin
 * synchronously, then clear the Memories origin. Avoid AuthContext.logout()
 * first — that races PrivateRoute into flashing login/home before navigate.
 */
export async function performMainAppCrossLogout(returnUrl) {
  if (typeof window === 'undefined') return;
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    try {
      await api.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Continue with local clear even if revoke fails
    }
  }
  clearMainAppLocalSession();
  window.location.assign(mainAppCrossLogoutUrl(sanitizeLogoutReturnUrl(returnUrl)));
}

/**
 * Hard-navigate logout from Memories: revoke all server sessions, clear this
 * origin synchronously, then clear the main-app origin. Avoid React setState
 * before navigate — that races PrivateRoute into re-bridging a main JWT.
 */
export async function performMemoriesCrossLogout() {
  if (typeof window === 'undefined') return;
  beginMemoriesLogout();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    try {
      await api.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Continue with local clear even if revoke fails
    }
  }
  clearMemoriesLocalSession();
  window.location.href = memoriesCrossLogoutUrl(`${mainAppOrigin()}/login`);
}

export function isMemoriesSharedAuthEnabled() {
  return typeof window !== 'undefined' && isMemoriesHost();
}

export function markHandoffComplete() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(HANDOFF_FLAG, '1');
  }
}

export function consumeHandoffComplete() {
  if (typeof sessionStorage === 'undefined') return false;
  const value = sessionStorage.getItem(HANDOFF_FLAG);
  sessionStorage.removeItem(HANDOFF_FLAG);
  return value === '1';
}
